import { paths } from './routes';

// [MCV-1549] `responseId` names the row to open. The listing filter alone only
// narrows the list; the surfaces then select their first row, so a link without
// it opens an arbitrary conversation on any listing with more than one
// response. Kept in step with mcv_api/services/notification_links.py, which
// builds the same strings for notification payloads — including param order.
type B2BOpportunitiesUrlParams = {
  view: 'sent' | 'received';
  listingId?: number;
  responseId?: number;
  // Which Buying | Selling tab of B2B Responses the row lives under.
  side?: 'buying' | 'selling';
};

type JobOpportunitiesUrlParams = {
  listingId?: number;
  responseId?: number;
};

type CalendarUrlParams = {
  serviceListingId?: number | 'general';
};

type ProviderViewUrlParams = (
  | {
      providerId: number;
      responseId: number;
      responseProviderId?: number;
      serviceListingId?: never;
    }
  | {
      responseProviderId: number;
      providerId?: number;
      responseId?: number;
      serviceListingId?: never;
    }
  | {
      // A masked view reached from a Search Providers result row: the provider
      // is resolved server-side, so no provider id is ever in the URL.
      serviceListingId: number;
      providerId?: never;
      responseId?: never;
      responseProviderId?: never;
    }
) & {
  // The B2B Responses tab the response came from, so the page can link back to the right one.
  side?: 'buying' | 'selling';
};

export const buildB2BOpportunitiesUrl = ({
  view,
  listingId,
  responseId,
  side,
}: B2BOpportunitiesUrlParams): string => {
  const params = new URLSearchParams({ view });
  if (listingId != null) {
    params.set('listingId', String(listingId));
  }
  if (responseId != null) {
    params.set('responseId', String(responseId));
  }
  if (side != null) {
    params.set('side', side);
  }

  return `${paths.hlx.b2bOpportunities}?${params.toString()}`;
};

// The B2B Listings page opens its "New listing" wizard on the side named here. 'buying' posts what
// the employer needs (SEEKING); 'selling' offers the employer's providers (OFFERING).
export const buildNewMsaasListingUrl = (side: 'buying' | 'selling'): string =>
  `${paths.hlx.msaasListings}?${new URLSearchParams({ new: side }).toString()}`;

export const buildJobOpportunitiesUrl = ({
  listingId,
  responseId,
}: JobOpportunitiesUrlParams = {}): string => {
  const params = new URLSearchParams();
  if (listingId != null) {
    params.set('listingId', String(listingId));
  }
  if (responseId != null) {
    params.set('responseId', String(responseId));
  }

  const query = params.toString();
  return query ? `${paths.hlx.jobOpportunities}?${query}` : paths.hlx.jobOpportunities;
};

export const buildCalendarUrl = ({ serviceListingId }: CalendarUrlParams = {}): string => {
  if (serviceListingId == null) return paths.hlx.calendar;
  const params = new URLSearchParams({ serviceListingId: String(serviceListingId) });
  return `${paths.hlx.calendar}?${params.toString()}`;
};

export const buildServiceOpportunitiesUrl = (responseId: number): string =>
  `${paths.hlx.serviceOpportunities}?${new URLSearchParams({ responseId: String(responseId) })}`;

export const buildContractUrl = (contractId: number): string =>
  `${paths.hlx.contracts}?${new URLSearchParams({ contractId: String(contractId) })}`;

/** [MCV-1954] The Contracts page opened on one tab (it otherwise opens on Active). */
export const buildContractsTabUrl = (tab: 'active' | 'upcoming' | 'past'): string =>
  `${paths.hlx.contracts}?${new URLSearchParams({ tab })}`;

export const buildProviderViewUrl = (params: ProviderViewUrlParams): string => {
  const searchParams = new URLSearchParams();

  if (params.responseProviderId != null) {
    searchParams.set('responseProviderId', String(params.responseProviderId));
  }
  if (params.providerId != null) {
    searchParams.set('providerId', String(params.providerId));
  }
  if (params.responseId != null) {
    searchParams.set('responseId', String(params.responseId));
  }
  if (params.serviceListingId != null) {
    searchParams.set('serviceListingId', String(params.serviceListingId));
  }
  if (params.side != null) {
    searchParams.set('side', params.side);
  }

  return `${paths.hlx.providerView}?${searchParams.toString()}`;
};
